package nl.appstuff.padelchecker.site.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import nl.appstuff.padelchecker.site.localization.SiteStrings
import org.jetbrains.compose.web.css.*

private data class HowItWorksStep(
    val title: String,
    val description: String,
)

private fun SiteStrings.steps(): List<HowItWorksStep> = listOf(
    HowItWorksStep(title = howItWorksStep1Title, description = howItWorksStep1Body),
    HowItWorksStep(title = howItWorksStep2Title, description = howItWorksStep2Body),
    HowItWorksStep(title = howItWorksStep3Title, description = howItWorksStep3Body),
)

@Composable
fun HowItWorksSection() {
    val strings = LocalSiteStrings.current
    val breakpoint = rememberBreakpoint()
    val isDesktop = breakpoint >= Breakpoint.MD
    var selectedStep by remember { mutableStateOf(0) }
    var hoveredStep by remember { mutableStateOf<Int?>(null) }
    val activeStep = hoveredStep ?: selectedStep
    val steps = strings.steps()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.rgb(0xFFFFFF))
            .padding(top = if (isDesktop) 42.px else 26.px, leftRight = 24.px, bottom = if (isDesktop) 52.px else 36.px),
        contentAlignment = Alignment.Center,
    ) {
        if (isDesktop) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(1120.px),
                horizontalArrangement = Arrangement.spacedBy(76.px),
                verticalAlignment = Alignment.Top,
            ) {
                TimelinePanel(
                    activeStep = activeStep,
                    selectedStep = selectedStep,
                    steps = steps,
                    onSelected = { selectedStep = it },
                    onHovered = { hoveredStep = it },
                    onHoverLeft = { hoveredStep = null },
                    modifier = Modifier.flex(1),
                )
                PhonePreview(step = activeStep, modifier = Modifier.flex(1))
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(560.px),
                verticalArrangement = Arrangement.spacedBy(28.px),
            ) {
                PhonePreview(step = activeStep)
                TimelinePanel(
                    activeStep = activeStep,
                    selectedStep = selectedStep,
                    steps = steps,
                    onSelected = { selectedStep = it },
                    onHovered = { hoveredStep = it },
                    onHoverLeft = { hoveredStep = null },
                )
            }
        }
    }
}

@Composable
private fun TimelinePanel(
    activeStep: Int,
    selectedStep: Int,
    steps: List<HowItWorksStep>,
    onSelected: (Int) -> Unit,
    onHovered: (Int) -> Unit,
    onHoverLeft: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val strings = LocalSiteStrings.current
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(10.px)) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                },
        ) {
            Box(
                modifier = Modifier
                    .width(2.px)
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "5px")
                        property("top", "8px")
                        property("bottom", "4px")
                    }
                    .background(SiteColors.blue),
            )
            Box(
                modifier = Modifier
                    .width(16.px)
                    .height(2.px)
                    .background(SiteColors.blue)
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "-2px")
                        property("bottom", "4px")
                    },
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 10.px),
                verticalArrangement = Arrangement.spacedBy(12.px),
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Box(
                        modifier = Modifier
                            .width(12.px)
                            .height(15.px),
                        contentAlignment = Alignment.Center,
                    ) {
                        Dot()
                    }
                    SpanText(
                        strings.howItWorksHeading,
                        modifier = Modifier
                            .fontSize(21.px)
                            .fontWeight(FontWeight.Bold)
                            .color(SiteColors.textLight),
                    )
                }

                steps.forEachIndexed { index, step ->
                    val isActive = index == activeStep
                    val isSelected = index == selectedStep
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .cursor(Cursor.Pointer)
                            .onClick { onSelected(index) }
                            .onMouseEnter { onHovered(index) }
                            .onMouseLeave { onHoverLeft() },
                        horizontalArrangement = Arrangement.spacedBy(11.px),
                        verticalAlignment = Alignment.Top,
                    ) {
                        Box(
                            modifier = Modifier
                                .margin(top = 6.px)
                                .width(12.px)
                                .height(12.px)
                                .background(if (isActive) SiteColors.blue else Color.rgb(0x8EB8F5))
                                .borderRadius(2.px)
                                .rotate(45.deg)
                                .styleModifier {
                                    property("transition", "background-color 180ms ease")
                                },
                        )
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .styleModifier { property("transition", "opacity 180ms ease") },
                            verticalArrangement = Arrangement.spacedBy(8.px),
                        ) {
                            SpanText(
                                step.title,
                                modifier = Modifier
                                    .fontSize(16.px)
                                    .fontWeight(FontWeight.Medium)
                                    .color(SiteColors.textLight),
                            )
                            Box(
                                modifier = Modifier
                                    .maxWidth(460.px)
                                    .background(if (isActive) Color.rgb(0xE9F0FC) else Color.rgb(0xFFFFFF))
                                    .border(
                                        1.px,
                                        LineStyle.Solid,
                                        if (isActive || isSelected) SiteColors.blue else Color.rgb(0xC9CED8),
                                    )
                                    .borderRadius(8.px)
                                    .padding(12.px)
                                    .styleModifier {
                                        property("transition", "all 180ms ease")
                                    },
                            ) {
                                SpanText(
                                    step.description,
                                    modifier = Modifier
                                        .fontSize(14.px)
                                        .lineHeight(1.35)
                                        .color(SiteColors.textMutedLight),
                                )
                            }
                        }
                    }
                }
            }
        }

        NotificationToast(show = activeStep == steps.lastIndex, strings = strings)
    }
}

@Composable
private fun NotificationToast(show: Boolean, strings: SiteStrings) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(100.px)
            .styleModifier {
                property("overflow", "hidden")
                property("position", "relative")
            },
    ) {
        Box(
            modifier = Modifier
                .maxWidth(520.px)
                .background(Color.rgb(0xF4F4F4))
                .borderRadius(22.px)
                .padding(leftRight = 24.px, top = 18.px, bottom = 18.px)
                .styleModifier {
                    property("position", "absolute")
                    property("left", "22px")
                    property("right", "0")
                    property("bottom", "0")
                    property("transform", if (show) "translateY(0)" else "translateY(96px)")
                    property("opacity", if (show) "1" else "0")
                    property("transition", "transform 320ms ease, opacity 320ms ease")
                },
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(10.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Image(
                            src = "/images/app_icon.png",
                            alt = strings.howItWorksAppLogoAlt,
                            modifier = Modifier
                                .borderRadius(2.px)
                                .width(18.px)
                                .height(18.px)
                                .styleModifier {
                                    property("object-fit", "contain")
                                },
                        )
                        SpanText(
                            strings.howItWorksToastBrand,
                            modifier = Modifier
                                .fontSize(12.px)
                                .fontWeight(FontWeight.SemiBold)
                                .color(Color.rgb(0xA0A0A0)),
                        )
                    }
                    SpanText(
                        strings.howItWorksToastTime,
                        modifier = Modifier
                            .fontSize(12.px)
                            .fontWeight(FontWeight.Medium)
                            .color(Color.rgb(0x9D9D9D)),
                    )
                }
                SpanText(
                    strings.howItWorksToastText,
                    modifier = Modifier
                        .fontSize(16.px)
                        .fontWeight(FontWeight.SemiBold)
                        .lineHeight(1.14)
                        .color(SiteColors.textLight),
                )
            }
        }
    }
}

private val phoneImages = listOf(
    "/images/stepper_1.png",
    "/images/stepper_2.png",
    "/images/stepper_3.png",
)

@Composable
private fun PhonePreview(step: Int, modifier: Modifier = Modifier) {
    val strings = LocalSiteStrings.current
    val phoneAspectRatio = "1000 / 1297"

    Box(
        modifier = modifier
            .fillMaxWidth()
            .styleModifier {
                property("display", "flex")
                property("justify-content", "center")
            },
    ) {
        Box(
            modifier = Modifier
                .maxWidth(390.px)
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("aspect-ratio", phoneAspectRatio)
                },
        ) {
            phoneImages.forEachIndexed { index, image ->
                Image(
                    src = image,
                    alt = when (index) {
                        0 -> strings.howItWorksPhoneAlt1
                        1 -> strings.howItWorksPhoneAlt2
                        else -> strings.howItWorksPhoneAlt3
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("width", "100%")
                            property("height", "100%")
                            property("object-fit", "contain")
                            property("opacity", if (index == step) "1" else "0")
                            property("transition", "opacity 220ms ease-in-out")
                        },
                )
            }
        }
    }
}

@Composable
private fun Dot() {
    Box(
        modifier = Modifier
            .width(15.px)
            .height(15.px)
            .background(SiteColors.blue)
            .borderRadius(50.percent),
    )
}
